import { ISupplierHotel } from '../../types/entities/ISupplierHotel';
import { ISupplierHotelRoomType } from '../../types/entities/ISupplierHotelRoomType';
import {
    ProgressArrayItem,
    ProgressState,
    PropertyState,
    RoomState,
} from '../../types/properties/propertyTypes';
import { convertDateToUTC } from '../../utils/formatDates';
import {
    getProgressStateFromDB,
    tabsData,
} from '../../utils/property/propertyUtils';
import { getRandomNumber } from '../../utils/usefulFunctions';

export const adaptDDBBToPropertyState = (
    supplierHotel: ISupplierHotel
): PropertyState => {
    return {
        id: supplierHotel.Id || '',
        name: supplierHotel.Hotel.Name || '',
        primary:
            supplierHotel.Hotel.SupplierHotels.find(
                supHotel =>
                    supHotel.IdSupplierHotel === parseInt(supplierHotel.Id)
            )?.Primary || false,
        idHotel: supplierHotel.IdHotel || 0,
        idSupplier: supplierHotel.IdSupplier || 0,
        idHotelCategory: supplierHotel.Hotel.IdHotelCategory || 0,
        idHotelChain: supplierHotel.Hotel.IdHotelChain || 0,
        roomCount: supplierHotel.Hotel.Rooms || 0,

        hotelCategory:
            supplierHotel.Hotel.HotelCategory.LocalizedCaption ||
            supplierHotel.Hotel.HotelCategory.Caption ||
            supplierHotel.Hotel.HotelCategory.Name ||
            '',
        rating: supplierHotel.Hotel.HotelCategory.RatingNumber || 0,
        idHotelType: supplierHotel.Hotel.IdHotelType || 0,
        idCurrency: supplierHotel.Hotel.IdCurrency || 0,
        checkInHour: supplierHotel.Hotel.CheckInHour || '',
        checkOutHour: supplierHotel.Hotel.CheckOutHour || '',
        address: supplierHotel.Hotel.Address || '',
        email: supplierHotel.Hotel.GeneralEmail || '',
        phone: supplierHotel.Hotel.GeneralPhone || '',
        bookingPhone: supplierHotel.Hotel.BookingPhone || '',
        bookingEmail: supplierHotel.Hotel.BookingEmail || '',
        accountingPhone: supplierHotel.Hotel.AccountingPhone || '',
        accountingEmail: supplierHotel.Hotel.AccountingEmail || '',
        idCountry: supplierHotel.Hotel.IdCountry || 0,
        idRegion: supplierHotel.Hotel.IdRegion || 0,
        idProvince: supplierHotel.Hotel.IdProvince || 0,
        idCity: supplierHotel.Hotel.IdCity || 0,
        postalCode: supplierHotel.Hotel.PostalCode || '',
        airportCode: supplierHotel.Hotel.HotelCodes.NearbyAirportCode || '',
        idTimeZone: supplierHotel.Hotel.IdTimeZoneUtil || 0,
        adultsOnly: supplierHotel.Hotel.AdultsOnly || false,
        adultAge: supplierHotel.Hotel.AdultAge || 0,
        babiesAge: supplierHotel.Hotel.BabiesAge || 0,
        firstChildAge: supplierHotel.Hotel.FirstChildAge || 0,
        secondChildAge: supplierHotel.Hotel.SecondChildAge || 0,
        descriptions: supplierHotel.Descriptions.filter(
            description => description.DescriptionType.Key === 'LONG'
        ).map(description => ({
            id: description.Id,
            idLanguage: description.IdContentLanguage || 0,
            languageCode: description.ContentLanguage.CultureId || '',
            language:
                description.ContentLanguage?.LocalizedCaption ||
                description.ContentLanguage?.Caption ||
                description.ContentLanguage?.Name ||
                '',
            descriptionContent: description.Description || '',
        })),
        services: supplierHotel.Services.map(service => ({
            id: service.Id,
            name:
                service?.LocalizedCaption ||
                service?.Caption ||
                service?.Name ||
                '',
            free: service.Free || false,
        })),
        hostSegments: supplierHotel.HostSegments.map(segment => ({
            id: segment.Id,
            name:
                segment?.LocalizedCaption ||
                segment?.Caption ||
                segment?.Name ||
                '',
        })),
        mealPlanTypes: supplierHotel.SupplierHotelMealPlans.filter(
            mealPlan => mealPlan.Enabled
        ).map(mealPlan => ({
            id: mealPlan.Id,
            name: mealPlan.Name || '',
            mappingCode: mealPlan.MappingCode || '',
            idType: parseInt(mealPlan.MealPlanType.Id) || 0,
            typeName:
                mealPlan.MealPlanType?.LocalizedCaption ||
                mealPlan.MealPlanType?.Caption ||
                mealPlan.MealPlanType?.Name ||
                '',
            typeMappingCodeTemplate:
                mealPlan.MealPlanType.MappingCodeTemplate || '',
        })),
        images: supplierHotel.SupplierHotelImages.map(image => ({
            id: image.Id,
            caption: image.Caption || '',
            feature: image.Feature || false,
            is360: image.Is360 || false,
            published: image.Published || false,
            idType: image.IdHotelImageType || 0,
            type: image.HotelImageType.Key || '',
            url: image.VirtualPath || '',
        })),
        rooms: supplierHotel.SupplierHotelRoomTypes.sort(
            (a, b) =>
                new Date(b.DateLastUpdated).getTime() -
                new Date(a.DateLastUpdated).getTime()
        ).map(room => adaptRoomDDBBToState(room)),
        notes: supplierHotel.SpecialNotes.sort(
            (a, b) =>
                new Date(b.DateLastUpdated).getTime() -
                new Date(a.DateLastUpdated).getTime()
        ).map(note => ({
            id: note.Id,
            message: note.Message || '',
            fromBookingDate: note.FromBookingDate || false,
            fromDate: note.FromDate || null,
            toDate: note.ToDate || null,
            enabled: note.Enabled || false,
        })),
    };
};

export const adaptDDBBToProgressState = (
    supplierHotel: ISupplierHotel
): Partial<ProgressState> => {
    const progressArray: ProgressArrayItem[] =
        supplierHotel.SupplierHotelContentWizardProgress.map(progress => ({
            id: progress.Id,
            step: progress.Step || '',
            tab: progress.Tab || '',
        }));

    if (supplierHotel.Progress >= 95) {
        Object.entries(tabsData).forEach(([stepKey, stepData]) => {
            stepData.tabs.forEach(tab => {
                const exists = progressArray.some(
                    p => p.step === stepKey && p.tab === tab.title
                );
                if (!exists) {
                    progressArray.push({
                        id: `-${getRandomNumber()}`,
                        step: stepKey,
                        tab: tab.title,
                        __isAdded: true,
                        __isChanged: true,
                    });
                }
            });
        });
    }

    return {
        totalProgress: supplierHotel.Progress || 0,
        progressByStep: getProgressStateFromDB(
            supplierHotel.SupplierHotelContentWizardProgress
        ),
        progressArray,
    };
};

export const adaptPropertyStateToDDBB = (
    propertyState: PropertyState,
    progress: ProgressState
) => {
    return {
        Id: propertyState.id,
        Name: propertyState.name,
        Progress: progress.totalProgress,
        SupplierHotelContentWizardProgress: progress.progressArray.map(
            progressItem => ({
                Id: progressItem.id,
                Step: progressItem.step || '',
                Tab: progressItem.tab || '',
                Completed: true,
                __isAdded: progressItem.__isAdded || false,
                __isChanged:
                    progressItem.__isChanged || progressItem.__isAdded || false,
            })
        ),
        Hotel: {
            Name: propertyState.name,
            IdHotelType: propertyState.idHotelType,
            IdHotelCategory: propertyState.idHotelCategory,
            IdHotelChain: propertyState.idHotelChain,
            IdCurrency: propertyState.idCurrency,
            IdCity: propertyState.idCity,
            IdProvince: propertyState.idProvince,
            IdRegion: propertyState.idRegion,
            IdCountry: propertyState.idCountry,
            IdTimeZoneUtil: propertyState.idTimeZone,
            GeneralPhone: propertyState.phone,
            GeneralEmail: propertyState.email,
            BookingPhone: propertyState.bookingPhone,
            BookingEmail: propertyState.bookingEmail,
            AccountingPhone: propertyState.accountingPhone,
            AccountingEmail: propertyState.accountingEmail,
            Address: propertyState.address,
            CheckInHour: propertyState.checkInHour,
            CheckOutHour: propertyState.checkOutHour,
            PostalCode: propertyState.postalCode,
            HotelCodes: { NearbyAirportCode: propertyState.airportCode },
            Rooms: propertyState.roomCount,
            AdultsOnly: propertyState.adultsOnly,
            AdultAge: propertyState.adultAge,
            BabiesAge: propertyState.babiesAge,
            FirstChildAge: propertyState.firstChildAge,
            SecondChildAge: propertyState.secondChildAge,
        },
        HostSegments: propertyState.hostSegments.map(segment => ({
            Id: segment.id,
            __isAdded: segment.__isAdded || false,
            Deleted: segment.Deleted || false,
        })),
        Services: propertyState.services.map(service => ({
            Id: service.id,
            __isAdded: service.__isAdded || false,
            Deleted: service.Deleted || false,
        })),
        SupplierHotelMealPlans: propertyState.mealPlanTypes.map(mealPlan => ({
            Id: mealPlan.id,
            Name: mealPlan.name,
            MappingCode: mealPlan.mappingCode,
            IdMealPlanType: mealPlan.idType,
            Enabled: true,
            __isAdded: mealPlan.__isAdded || false,
            __isChanged: mealPlan.__isChanged || false,
            Deleted: mealPlan.Deleted || false,
        })),
        Descriptions: propertyState.descriptions.map(description => ({
            Id: description.id,
            IdContentLanguage: description.idLanguage,
            IdDescriptionType: 2, //LONG description type
            Description: description.descriptionContent,
            IdSupplierHotel: parseInt(propertyState.id),
            IdSupplier: propertyState.idSupplier,
            __isAdded: description.__isAdded || false,
            __isChanged:
                description.__isChanged || description.__isAdded || false,
            Deleted: description.Deleted || false,
        })),
        SupplierHotelRoomTypes: propertyState.rooms.map(room => ({
            Id: room.id,
            Name: room.name,
            Description: room.description,
            AdultOnly: room.adultsOnly,
            FreeBaby1: room.freeBaby1,
            FreeBaby2: room.freeBaby2,
            Occupancy: room.occupancy,
            StandardOccupancy: room.standardOccupancy,
            MinAdults: room.minAdults,
            MaxAdults: room.maxAdults,
            MinChildren: room.minChildren,
            MaxChild: room.maxChildren,
            Size: room.size,
            Quantity: room.quantity,
            Services: room.services.map(service => ({
                Id: service.id,
                __isAdded: service.__isAdded || false,
                Deleted: service.Deleted || false,
            })),
            SupplierHotelRoomBedTypes: room.bedTypes.map(bedType => ({
                Id: bedType.id,
                __isAdded: bedType.__isAdded || false,
                __isChanged: bedType.__isChanged || bedType.__isAdded || false,
                Deleted: bedType.Deleted || false,
            })),
            __isChanged: room.__isChanged || false,
        })),
        SpecialNotes: propertyState.notes.map(note => ({
            Id: note.id,
            FromDate: note.fromDate ? convertDateToUTC(note.fromDate) : null,
            ToDate: note.toDate ? convertDateToUTC(note.toDate) : null,
            FromBookingDate: note.fromBookingDate,
            Message: note.message,
            Enabled: note.enabled,
            __isAdded: note.__isAdded || false,
            __isChanged: note.__isChanged || note.__isAdded || false,
            Deleted: note.Deleted || false,
        })),
        // necesario para que se grabe bien
        DateLastUpdated: new Date().toISOString(),
    };
};

export const adaptRoomDDBBToState = (
    room: ISupplierHotelRoomType
): RoomState => {
    return {
        id: room.Id,
        name: room.Name || '',
        description: room.Description || '',
        adultsOnly: room.AdultOnly || false,
        freeBaby1: true,
        freeBaby2: true,
        occupancy: room.Occupancy || '',
        standardOccupancy: room.StandardOccupancy || 0,
        minAdults: room.MinAdults || 0,
        maxAdults: room.MaxAdults || 0,
        minChildren: room.MinChildren || 0,
        maxChildren: room.MaxChild || 0,
        size: room.Size || 0,
        quantity: room.Quantity || 0,
        images: room.SupplierHotelRoomTypesImages.map(image => ({
            id: image.Id,
            caption: image.Caption || '',
            feature: image.Feature || false,
            is360: image.Is360 || false,
            published: image.Published || false,
            type: image.HotelImageType.Key || '',
            idType: image.IdHotelImageType || 0,
            url: image.VirtualPath || '',
        })),
        services: room.Services.map(service => ({
            id: service.Id,
            name:
                service?.LocalizedCaption ||
                service?.Caption ||
                service?.Name ||
                '',
            free: service.Free || false,
        })),
        bedTypes: room.SupplierHotelRoomBedTypes.map(bedType => ({
            id: bedType.Id,
            name:
                bedType?.LocalizedCaption ||
                bedType?.Caption ||
                bedType?.Name ||
                '',
        })),
    };
};

export const adaptRoomToDDBB = (room: RoomState) => {
    return {
        Id: room.id,
        Name: room.name,
        Description: room.description,
        AdultOnly: room.adultsOnly,
        FreeBaby1: room.freeBaby1,
        FreeBaby2: room.freeBaby2,
        Occupancy: room.occupancy,
        StandardOccupancy: room.standardOccupancy,
        MinAdults: room.minAdults,
        MaxAdults: room.maxAdults,
        MinChildren: room.minChildren,
        MaxChild: room.maxChildren,
        Size: room.size,
        Quantity: room.quantity,
        Services: room.services.map(service => ({
            Id: service.id,
            __isAdded: service.__isAdded || false,
            Deleted: service.Deleted || false,
        })),
        SupplierHotelRoomBedTypes: room.bedTypes.map(bedType => ({
            Id: bedType.id,
            __isAdded: bedType.__isAdded || false,
            __isChanged: bedType.__isChanged || bedType.__isAdded || false,
            Deleted: bedType.Deleted || false,
        })),
    };
};
